import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  cancelReceipt,
  closeReceipt,
  createReceipt,
  fetchReceipt,
  fetchReceiptManifest,
  fetchReceipts,
  receiveReceipt,
  updateReceipt,
  uploadReceipts,
} from './receiptsThunks';
import { ALL_FILTER } from './receiptsConstants';
import { selectWarehouse } from '../warehouseContext/warehouseContextSlice';

const initialState = {
  /** Current page of receiving orders. */
  receipts: [],
  totalCount: 0,
  /**
   * How many orders there are in each status, over the other filters and not
   * over the page. `null` until the server says: a row of zeros would read as
   * "there is nothing".
   */
  statusCounts: null,
  /** Whether this session may count, close and void in the warehouse in use. */
  canConfirm: false,
  loading: false,
  error: null,
  /** The request whose answer may still write. See `fetchReceipts.fulfilled`. */
  requestId: null,
  filters: {
    status: ALL_FILTER,
    company: ALL_FILTER,
    /** Where the receipt came from: `RECEIPT_SOURCE_OPTIONS`. */
    source: ALL_FILTER,
    search: '',
    page: 1,
    rowsPerPage: 20,
  },

  /** The order open on its own page. */
  detail: null,
  /** Whether this session may confirm in the warehouse of `detail`. */
  detailCanConfirm: false,
  detailLoading: false,
  detailError: null,

  /** A declaration is being saved. Its failure is shown inside the form. */
  saving: false,
  /** `'receive'`, `'close'` or `'cancel'` while that is on its way. */
  acting: null,
  uploading: false,
  printing: false,
};

const receiptsSlice = createSlice({
  name: 'receipts',
  initialState,
  reducers: {
    /** Merges a patch into the filters; anything but a page change goes back to page 1. */
    setReceiptFilters: (state, action) => {
      state.filters = { ...state.filters, page: 1, ...action.payload };
    },
    /** Leaving the detail: the next order must not open on this one. */
    clearReceiptDetail: (state) => {
      state.detail = null;
      state.detailCanConfirm = false;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReceipts.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchReceipts.fulfilled, (state, action) => {
        // Only the latest request may write: a write reloads the listing while
        // a filter may still be on its way.
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.receipts = action.payload.results || [];
        state.totalCount = action.payload.count || 0;
        state.statusCounts = action.payload.status_counts || null;
        state.canConfirm = Boolean(action.payload.can_confirm);
      })
      .addCase(fetchReceipts.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload;
        state.receipts = [];
        state.totalCount = 0;
        state.statusCounts = null;
      })

      .addCase(fetchReceipt.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchReceipt.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload.receipt;
        state.detailCanConfirm = action.payload.canConfirm;
      })
      .addCase(fetchReceipt.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
      })

      .addCase(receiveReceipt.pending, (state) => {
        state.acting = 'receive';
      })
      .addCase(receiveReceipt.fulfilled, (state, action) => {
        state.acting = null;
        if (state.detail?.id === action.payload.receipt.id) state.detail = action.payload.receipt;
      })
      .addCase(closeReceipt.pending, (state) => {
        state.acting = 'close';
      })
      .addCase(cancelReceipt.pending, (state) => {
        state.acting = 'cancel';
      })

      .addCase(uploadReceipts.pending, (state) => {
        state.uploading = true;
      })
      .addCase(fetchReceiptManifest.pending, (state) => {
        state.printing = true;
      })

      .addCase(selectWarehouse, (state) => {
        // Another warehouse is another set of clients, and of orders.
        state.filters.company = ALL_FILTER;
        state.filters.page = 1;
      })

      // A declaration, saved or refused.
      .addMatcher(isAnyOf(createReceipt.pending, updateReceipt.pending), (state) => {
        state.saving = true;
      })
      .addMatcher(
        isAnyOf(
          createReceipt.fulfilled,
          createReceipt.rejected,
          updateReceipt.fulfilled,
          updateReceipt.rejected
        ),
        (state) => {
          state.saving = false;
        }
      )
      // The writes that answer the whole order: the open one takes the answer.
      .addMatcher(
        isAnyOf(updateReceipt.fulfilled, closeReceipt.fulfilled, cancelReceipt.fulfilled),
        (state, action) => {
          if (state.detail?.id === action.payload.id) state.detail = action.payload;
        }
      )
      // None of their failures is stored: the dialog that asked is still open
      // and shows it there, while `error` would replace the screen with it.
      .addMatcher(
        isAnyOf(
          receiveReceipt.rejected,
          closeReceipt.fulfilled,
          closeReceipt.rejected,
          cancelReceipt.fulfilled,
          cancelReceipt.rejected
        ),
        (state) => {
          state.acting = null;
        }
      )
      .addMatcher(isAnyOf(uploadReceipts.fulfilled, uploadReceipts.rejected), (state) => {
        state.uploading = false;
      })
      .addMatcher(
        isAnyOf(fetchReceiptManifest.fulfilled, fetchReceiptManifest.rejected),
        (state) => {
          state.printing = false;
        }
      );
  },
});

export const { setReceiptFilters, clearReceiptDetail } = receiptsSlice.actions;

export default receiptsSlice.reducer;
